import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useDeleteClassCountTuitionBilling } from '~entities/payment/api/class-count-tuition/delete-class-count-tuition-billing';
import {
  type ClassCountTuitionBilling,
  useGetClassCountTuitionBilling,
} from '~entities/payment/api/class-count-tuition/get-class-count-tuition-billing';

import { Button } from '~shared/shadcn/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';

import { ClassCountChartFormDialog } from './class-count-chart-form-dialog';

const formatBands = (chart: ClassCountTuitionBilling) =>
  chart.ranges
    .map((range) => {
      const countLabel =
        range.minClasses === range.maxClasses
          ? `${range.minClasses}`
          : `${range.minClasses}–${range.maxClasses}`;
      return `${countLabel} → $${range.flatRate}`;
    })
    .join(' · ');

interface ClassCountChartsPanelProps {
  /** Current studio default chart id, if any. */
  studioDefaultChartId?: string | null;
  /** Clear studio default when the deleted chart was the default. */
  onClearStudioDefault?: () => Promise<void>;
}

export const ClassCountChartsPanel = ({
  studioDefaultChartId,
  onClearStudioDefault,
}: ClassCountChartsPanelProps) => {
  const { data: charts = [], isLoading } = useGetClassCountTuitionBilling();
  const { mutateAsync: deleteChart, isPending: isDeleting } =
    useDeleteClassCountTuitionBilling();
  const [formOpen, setFormOpen] = useState(false);
  const [editingChart, setEditingChart] =
    useState<ClassCountTuitionBilling | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<ClassCountTuitionBilling | null>(null);

  const openCreate = () => {
    setEditingChart(null);
    setFormOpen(true);
  };

  const openEdit = (chart: ClassCountTuitionBilling) => {
    setEditingChart(chart);
    setFormOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const deletedId = deleteTarget._id;
    const wasStudioDefault = studioDefaultChartId === deletedId;
    try {
      await deleteChart(deletedId);
      if (wasStudioDefault && onClearStudioDefault) {
        await onClearStudioDefault();
      }
      toast.success('Rate chart deleted');
      setDeleteTarget(null);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Could not delete chart',
      );
    }
  };

  return (
    <section className='space-y-4'>
      <div className='flex flex-wrap items-end justify-between gap-3'>
        <div>
          <h3 className='text-lg font-semibold text-[#1F1F1F]'>Rate charts</h3>
          <p className='text-sm text-muted-foreground'>
            Assign a chart on monthly classes, or set a studio default above.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className='mr-1 size-4' />
          New chart
        </Button>
      </div>

      {isLoading ? (
        <div className='flex items-center gap-2 py-10 text-sm text-muted-foreground'>
          <Loader2 className='size-4 animate-spin' />
          Loading charts…
        </div>
      ) : charts.length === 0 ? (
        <div className='rounded-xl border border-dashed px-6 py-10 text-center'>
          <p className='font-medium text-[#1F1F1F]'>No rate charts yet</p>
          <p className='mt-1 text-sm text-muted-foreground'>
            Start with a sample chart (1 class $86, 2 classes $162).
          </p>
          <Button className='mt-4' onClick={openCreate}>
            Create first chart
          </Button>
        </div>
      ) : (
        <div className='overflow-hidden rounded-xl border'>
          <table className='w-full text-left text-sm'>
            <thead className='border-b bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground'>
              <tr>
                <th className='px-4 py-3 font-medium'>Name</th>
                <th className='px-4 py-3 font-medium'>Bands</th>
                <th className='px-4 py-3 font-medium text-right'>Actions</th>
              </tr>
            </thead>
            <tbody className='divide-y'>
              {charts.map((chart) => (
                <tr key={chart._id} className='bg-white'>
                  <td className='px-4 py-3 font-medium text-[#1F1F1F]'>
                    {chart.name}
                  </td>
                  <td className='px-4 py-3 text-muted-foreground'>
                    {formatBands(chart)}
                  </td>
                  <td className='px-4 py-3'>
                    <div className='flex justify-end gap-1'>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        onClick={() => {
                          openEdit(chart);
                        }}
                      >
                        <Pencil className='size-4' />
                      </Button>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='text-destructive'
                        onClick={() => {
                          setDeleteTarget(chart);
                        }}
                      >
                        <Trash2 className='size-4' />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ClassCountChartFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditingChart(null);
        }}
        chart={editingChart}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleteTarget?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              {studioDefaultChartId === deleteTarget?._id
                ? 'This chart is the studio default. Deleting it will clear the default preference.'
                : 'Classes still pointing at this chart will fall back to the studio default (if set) or stop using family class-count pricing.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeleting}
              onClick={() => {
                void confirmDelete();
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
